import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Camera, Signature, ShieldCheck, Save, Stethoscope } from "lucide-react";

const ROLES_THAT_NEED_SIGNATURE = ["doctor", "optometrist", "pathologist"];

export default function Profile() {
  const { user: ctxUser } = useAuth();
  const [me, setMe] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", qualification: "", registration_no: "" });
  const [photo, setPhoto] = useState("");
  const [sig, setSig] = useState("");
  const [saving, setSaving] = useState(false);
  const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });

  const load = async () => {
    const { data } = await api.get("/auth/me");
    setMe(data.user);
    setForm({
      name: data.user.name || "", phone: data.user.phone || "",
      qualification: data.user.qualification || "", registration_no: data.user.registration_no || "",
    });
    setPhoto(data.user.photo_url || "");
    setSig(data.user.signature_url || "");
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const upload = async (file, scope) => {
    if (!file) return;
    if (!["image/jpeg", "image/png"].includes(file.type)) {
      toast.error("Please upload a JPG or PNG image.");
      return;
    }
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post(`/files/upload-logo?scope=${scope}&target_id=${me.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (scope === "user_photo") setPhoto(data.url);
      else setSig(data.url);
      toast.success(`${scope === "user_photo" ? "Photo" : "Signature"} uploaded (${data.width}×${data.height})`);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Upload failed"); }
  };

  const save = async () => {
    if (!me) return;
    setSaving(true);
    try {
      await api.patch(`/auth/me`, form);
      toast.success("Profile updated");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const changePassword = async () => {
    if (!pwd.current) { toast.error("Enter your current password."); return; }
    if (!pwd.next || pwd.next.length < 8) { toast.error("New password must be at least 8 characters."); return; }
    if (pwd.next === pwd.current) { toast.error("New password must differ from current password."); return; }
    if (pwd.next !== pwd.confirm) { toast.error("Passwords don't match."); return; }
    try {
      await api.post("/password/change", { current_password: pwd.current, new_password: pwd.next });
      setPwd({ current: "", next: "", confirm: "" });
      toast.success("Password changed");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  if (!me) return <div className="text-sm text-ink-muted">Loading profile…</div>;
  const needsSig = ROLES_THAT_NEED_SIGNATURE.includes(me.role) || !!me.is_doctor;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="profile-page">
      <div>
        <div className="section-label">My Account</div>
        <h1 className="font-heading text-3xl font-semibold">Profile</h1>
        <p className="mt-1 text-sm text-ink-muted">Update your details, profile photo and signature. Signature auto-stamps on printed prescriptions.</p>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Identity card */}
        <Card data-testid="profile-identity-card">
          <CardHeader>
            <CardTitle className="font-heading text-base flex items-center gap-2"><Camera size={16} className="text-brand" /> Identity</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col items-center gap-2">
              {photo
                ? <img src={photo} alt="Your photo" className="h-28 w-28 rounded-full border border-border object-cover" data-testid="profile-photo-preview" />
                : <div className="grid h-28 w-28 place-items-center rounded-full border border-dashed border-border bg-secondary text-xs text-ink-muted">No photo</div>}
              <Label htmlFor="photo-file" className="cursor-pointer text-xs text-brand underline-offset-2 hover:underline">Upload photo (JPG/PNG)</Label>
              <Input id="photo-file" type="file" accept="image/jpeg,image/png" className="hidden" data-testid="profile-photo-upload"
                onChange={(e) => upload(e.target.files?.[0], "user_photo")} />
            </div>
            <div className="rounded-md border border-border bg-secondary/40 p-3 text-xs">
              <div className="flex items-center justify-between"><span className="text-ink-muted">Email</span><span className="font-medium">{me.email}</span></div>
              <div className="mt-1 flex items-center justify-between"><span className="text-ink-muted">Role</span><span className="font-medium">{me.role.replace(/_/g, " ")}</span></div>
            </div>
          </CardContent>
        </Card>

        {/* Signature card (only clinical roles) */}
        {needsSig ? (
          <Card data-testid="profile-signature-card">
            <CardHeader>
              <CardTitle className="font-heading text-base flex items-center gap-2"><Signature size={16} className="text-brand" /> Signature</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="rounded-md border border-border bg-white p-3">
                {sig
                  ? <img src={sig} alt="Signature" className="mx-auto h-24 object-contain" data-testid="profile-signature-preview" />
                  : <div className="py-6 text-center text-xs text-ink-muted">No signature on file</div>}
              </div>
              <Label htmlFor="sig-file" className="block cursor-pointer text-xs text-brand underline-offset-2 hover:underline">Upload signature image (JPG/PNG)</Label>
              <Input id="sig-file" type="file" accept="image/jpeg,image/png" data-testid="profile-signature-upload"
                onChange={(e) => upload(e.target.files?.[0], "user_signature")} />
              <div className="rounded-md border border-leaf/30 bg-leaf-50 p-2 text-[11px] text-leaf-700">
                <ShieldCheck size={12} className="mr-1 inline" /> Auto-stamped on printed prescriptions for NABH compliance.
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card data-testid="profile-no-signature-card">
            <CardHeader>
              <CardTitle className="font-heading text-base flex items-center gap-2"><Stethoscope size={16} className="text-brand" /> Signature</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-ink-muted">Signature uploads are available for clinical roles (doctor, optometrist, pathologist) whose names appear on printed reports.</p>
            </CardContent>
          </Card>
        )}

        {/* Details + password */}
        <Card className="lg:col-span-3" data-testid="profile-details-card">
          <CardHeader>
            <CardTitle className="font-heading text-base">Personal details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <div><Label>Full name</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="profile-input-name" /></div>
              <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="profile-input-phone" /></div>
              <div><Label>Qualification (e.g. MBBS, MS Ophth)</Label><Input value={form.qualification} onChange={(e) => set("qualification", e.target.value)} data-testid="profile-input-qualification" /></div>
              <div><Label>Registration / Council no.</Label><Input value={form.registration_no} onChange={(e) => set("registration_no", e.target.value)} data-testid="profile-input-regno" /></div>
            </div>
            <Button onClick={save} disabled={saving} className="bg-brand hover:bg-brand-700" data-testid="profile-save"><Save size={14} className="mr-2" />{saving ? "Saving…" : "Save changes"}</Button>

            <div className="mt-6 border-t border-border pt-4">
              <div className="mb-2 text-sm font-semibold">Change password</div>
              <div className="grid gap-3 sm:grid-cols-3">
                <div><Label>Current password</Label><Input type="password" value={pwd.current} onChange={(e) => setPwd({ ...pwd, current: e.target.value })} data-testid="profile-pwd-current" autoComplete="current-password" /></div>
                <div><Label>New password (min 8 chars)</Label><Input type="password" value={pwd.next} onChange={(e) => setPwd({ ...pwd, next: e.target.value })} data-testid="profile-pwd-new" autoComplete="new-password" /></div>
                <div><Label>Confirm new password</Label><Input type="password" value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })} data-testid="profile-pwd-confirm" autoComplete="new-password" /></div>
              </div>
              <Button variant="outline" className="mt-3" onClick={changePassword} data-testid="profile-pwd-save">Update password</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
